/**
 * WerkstattPortal — Eigenes Dashboard für Werkstatt-Partner
 * 
 * DESIGN: Sauber, professionell, minimalistisch. Old Turquoise Akzente.
 * Separates Layout ohne SV-Büro-Navigation.
 * Werkstatt sieht NUR ihre eigenen Fälle.
 * 
 * Features v11:
 * - Benachrichtigungsglocke mit Realtime-Updates
 * - E-Mail-Login (Supabase Auth)
 * - Wissen & News Tab
 * - Dashboard, Fälle, Neue Anfrage, Profil
 */
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  useWerkstattPortal,
  useWerkstattBenachrichtigungen,
  useWerkstattWissen,
  type WerkstattFall,
  type WerkstattAnfrage,
  type WerkstattBenachrichtigung,
  type WerkstattWissen,
} from '@/hooks/useWerkstattPortal';
import { toast } from 'sonner';
import { useMobileDetailBackHandler } from '@/hooks/useMobileBackHandler';
import {
  LayoutDashboard, FolderOpen, Plus, User, LogOut, Search,
  FileText, MessageSquare, Download, Clock, CheckCircle2,
  AlertCircle, ChevronRight, Send, Building2, Car, Users,
  Shield, Phone, Mail, MapPin, Loader2, ArrowLeft, Eye,
  FileCheck, Calendar, Wrench, X, Bell, BellDot, BookOpen,
  ExternalLink, Tag, ChevronDown, Lock, KeyRound
} from 'lucide-react';

// ============ WERKSTATT LOGIN (E-Mail + Passwort) ============
export function WerkstattLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showReset, setShowReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { setError('Bitte E-Mail und Passwort eingeben.'); return; }
    setLoading(true);
    setError(null);
    const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (authError) {
      if (authError.message.includes('Invalid login')) {
        setError('E-Mail oder Passwort ist falsch.');
      } else if (authError.message.includes('Email not confirmed')) {
        setError('Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse.');
      } else {
        setError('Login fehlgeschlagen. Bitte prüfen Sie Ihre Zugangsdaten.');
      }
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) { setError('Bitte geben Sie Ihre E-Mail-Adresse ein.'); return; }
    setLoading(true);
    setError(null);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/werkstatt/login?reset=true`,
    });
    setLoading(false);
    if (resetError) {
      setError('Fehler beim Zurücksetzen. Bitte versuchen Sie es erneut.');
    } else {
      setResetSent(true);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#B91C1C] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Wrench className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Werkstatt-Portal</h1>
          <p className="text-sm text-gray-500 mt-1">SV Büro Koop — Partner-Zugang</p>
        </div>

        {/* Login / Reset Form */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          {showReset ? (
            <>
              <div className="flex items-center gap-2 mb-1">
                <KeyRound className="w-5 h-5 text-[#B91C1C]" />
                <h2 className="text-lg font-semibold text-gray-900">Passwort zurücksetzen</h2>
              </div>
              <p className="text-sm text-gray-500 mb-6">Geben Sie Ihre E-Mail-Adresse ein. Sie erhalten einen Link zum Zurücksetzen.</p>

              {resetSent ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-sm text-green-800 font-medium">E-Mail wurde gesendet!</p>
                  <p className="text-xs text-green-600 mt-1">Prüfen Sie Ihren Posteingang und klicken Sie auf den Link.</p>
                  <button onClick={() => { setShowReset(false); setResetSent(false); }} className="text-sm text-[#B91C1C] hover:underline mt-4">
                    Zurück zum Login
                  </button>
                </div>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail-Adresse</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                        placeholder="werkstatt@beispiel.de"
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none" />
                    </div>
                  </div>
                  <button type="submit" disabled={loading}
                    className="w-full bg-[#B91C1C] text-white py-2.5 rounded-lg text-sm font-medium hover:bg-[#991B1B] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Link senden
                  </button>
                  <button type="button" onClick={() => { setShowReset(false); setError(null); }}
                    className="w-full text-sm text-gray-500 hover:text-gray-700">
                    Zurück zum Login
                  </button>
                </form>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <Lock className="w-5 h-5 text-[#B91C1C]" />
                <h2 className="text-lg font-semibold text-gray-900">Anmelden</h2>
              </div>
              <p className="text-sm text-gray-500 mb-6">Melden Sie sich mit Ihren Werkstatt-Zugangsdaten an.</p>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail-Adresse</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                      placeholder="werkstatt@beispiel.de"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Passwort</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none" />
                  </div>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full bg-[#B91C1C] text-white py-2.5 rounded-lg text-sm font-medium hover:bg-[#991B1B] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Anmelden
                </button>
                <button type="button" onClick={() => { setShowReset(true); setError(null); }}
                  className="w-full text-sm text-gray-500 hover:text-[#B91C1C] transition-colors">
                  Passwort vergessen?
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-xs text-gray-400 text-center mt-6">
          SV Büro Koop · Rahmedestraße 65 · 58507 Lüdenscheid
        </p>
      </div>
    </div>
  );
}

// ============ WERKSTATT PORTAL (Main) ============
type PortalTab = 'dashboard' | 'faelle' | 'anfrage' | 'profil' | 'wissen';

export default function WerkstattPortal() {
  const { user, signOut } = useAuth();
  const [werkstattId, setWerkstattId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<PortalTab>('dashboard');
  const [selectedFall, setSelectedFall] = useState<WerkstattFall | null>(null);
  const [identLoading, setIdentLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);

  // Mobile: Hardware-Back schließt Fall-Detail statt Seite zu verlassen
  useMobileDetailBackHandler(!!selectedFall, () => setSelectedFall(null));

  // Identify werkstatt by user_id
  useEffect(() => {
    if (!user) return;
    const identify = async () => {
      let { data } = await supabase
        .from('werkstatt_partner')
        .select('id')
        .eq('user_id', user.id)
        .eq('aktiv', true)
        .single();
      if (!data) {
        const { data: byEmail } = await supabase
          .from('werkstatt_partner')
          .select('id')
          .eq('email', user.email)
          .eq('aktiv', true)
          .single();
        data = byEmail;
      }
      if (data) setWerkstattId(data.id);
      setIdentLoading(false);
    };
    identify();
  }, [user]);

  const { werkstatt, faelle, nachrichten, anfragen, loading, sendNachricht, createAnfrage, updateProfil, refresh } = useWerkstattPortal(werkstattId || undefined);
  const { benachrichtigungen, ungelesen, markAsRead, markAllAsRead } = useWerkstattBenachrichtigungen(werkstattId || undefined);

  if (identLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#B91C1C] mx-auto mb-3" />
          <p className="text-sm text-gray-500">Portal wird geladen...</p>
        </div>
      </div>
    );
  }

  if (!werkstattId || !werkstatt) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Kein Werkstatt-Zugang</h2>
          <p className="text-sm text-gray-500 mb-4">
            Ihr Benutzerkonto ist keiner Werkstatt zugeordnet. Bitte kontaktieren Sie SV Büro Koop.
          </p>
          <button onClick={() => signOut()} className="text-sm text-[#B91C1C] hover:underline">Abmelden</button>
        </div>
      </div>
    );
  }

  const offeneFaelle = faelle.filter(f => f.status === 'neu' || f.status === 'in_bearbeitung').length;
  const inBearbeitung = faelle.filter(f => f.status === 'in_bearbeitung').length;
  const abgeschlossen = faelle.filter(f => f.status === 'abgeschlossen').length;
  const ungeleseneNachrichten = nachrichten.filter(n => !n.gelesen && n.absender === 'sv_buero').length;
  const offeneAnfragen = anfragen.filter(a => a.status === 'neu').length;

  const NAV_ITEMS: { id: PortalTab; label: string; icon: any; badge?: number }[] = [
    { id: 'dashboard', label: 'Übersicht', icon: LayoutDashboard },
    { id: 'faelle', label: 'Meine Fälle', icon: FolderOpen, badge: offeneFaelle || undefined },
    { id: 'wissen', label: 'Wissen & News', icon: BookOpen },
    { id: 'anfrage', label: 'Neue Anfrage', icon: Plus },
    { id: 'profil', label: 'Profil', icon: User },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#B91C1C] rounded-lg flex items-center justify-center">
              <Wrench className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-gray-900 leading-none">{werkstatt.name}</h1>
              <p className="text-xs text-gray-400">Werkstatt-Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Benachrichtigungsglocke */}
            <div className="relative">              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                title="Benachrichtigungen"
              >
                {ungelesen > 0 ? <BellDot className="w-5 h-5 text-[#B91C1C]" /> : <Bell className="w-5 h-5" />}
                {ungelesen > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#B91C1C] text-white text-[10px] font-bold w-4.5 h-4.5 flex items-center justify-center rounded-full min-w-[18px] px-1">
                    {ungelesen > 9 ? '9+' : ungelesen}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              {showNotifications && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} />
                  <div className="absolute right-0 sm:right-0 left-0 sm:left-auto top-full mt-2 w-full sm:w-96 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-900">Benachrichtigungen</h3>
                      {ungelesen > 0 && (
                        <button onClick={() => { markAllAsRead(); }} className="text-xs text-[#B91C1C] hover:underline">
                          Alle als gelesen markieren
                        </button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {benachrichtigungen.length === 0 ? (
                        <div className="px-4 py-8 text-center">
                          <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                          <p className="text-sm text-gray-400">Keine Benachrichtigungen</p>
                        </div>
                      ) : (
                        benachrichtigungen.slice(0, 15).map(b => (
                          <button
                            key={b.id}
                            onClick={() => { markAsRead(b.id); setShowNotifications(false); }}
                            className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${!b.gelesen ? 'bg-blue-50/50' : ''}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${getNotifIconBg(b.typ)}`}>
                                {getNotifIcon(b.typ)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm ${!b.gelesen ? 'font-semibold text-gray-900' : 'text-gray-700'} truncate`}>{b.titel}</p>
                                {b.text && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{b.text}</p>}
                                <p className="text-[10px] text-gray-400 mt-1">{formatRelativeTime(b.created_at)}</p>
                              </div>
                              {!b.gelesen && <div className="w-2 h-2 bg-[#B91C1C] rounded-full shrink-0 mt-2" />}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <button
              onClick={() => signOut()}
              className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              title="Abmelden"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Navigation (Desktop/Tablet) */}
      <nav className="hidden sm:block bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto pb-2 sm:pb-0">
          {NAV_ITEMS.map(item => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setSelectedFall(null); }}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === item.id
                  ? 'border-[#B91C1C] text-[#B91C1C]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
              {item.badge && item.badge > 0 && (
                <span className="bg-gray-100 text-gray-600 text-xs px-1.5 py-0.5 rounded-full">{item.badge}</span>
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex-1 pb-24 sm:pb-6">
        {selectedFall ? (
          <FallDetail
            fall={selectedFall}
            nachrichten={nachrichten.filter(n => n.akte_id === selectedFall.akte_id)}
            onBack={() => setSelectedFall(null)}
            onSendMessage={(msg) => sendNachricht(selectedFall.akte_id, msg)}
            werkstattName={werkstatt.name}
          />
        ) : activeTab === 'dashboard' ? (
          <DashboardTab
            faelle={faelle} anfragen={anfragen}
            offeneFaelle={offeneFaelle} inBearbeitung={inBearbeitung}
            abgeschlossen={abgeschlossen} offeneAnfragen={offeneAnfragen}
            ungeleseneNachrichten={ungeleseneNachrichten}
            benachrichtigungen={benachrichtigungen}
            onSelectFall={f => { setSelectedFall(f); setActiveTab('faelle'); }}
            onNewAnfrage={() => setActiveTab('anfrage')}
          />
        ) : activeTab === 'faelle' ? (
          <FaelleTab faelle={faelle} onSelectFall={setSelectedFall} />
        ) : activeTab === 'wissen' ? (
          <WissenTab />
        ) : activeTab === 'anfrage' ? (
          <AnfrageTab onSubmit={createAnfrage} onSuccess={() => { setActiveTab('dashboard'); refresh(); }} />
        ) : activeTab === 'profil' ? (
          <ProfilTab werkstatt={werkstatt} onUpdate={updateProfil} />
        ) : null}
      </main>

      {/* Bottom Navigation (Mobile) */}
      <nav className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 pb-safe">
        <div className="flex items-center justify-around h-16 px-2">
          {NAV_ITEMS.map(item => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setSelectedFall(null); }}
              className={`flex flex-col items-center justify-center w-full h-full space-y-1 relative ${
                activeTab === item.id ? 'text-[#B91C1C]' : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.label}</span>
              {item.badge && item.badge > 0 && (
                <span className="absolute top-1 right-2 bg-[#B91C1C] text-white text-[8px] font-bold w-4 h-4 flex items-center justify-center rounded-full">
                  {item.badge > 9 ? '9+' : item.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* Footer */}
      <footer className="hidden sm:block border-t border-gray-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 text-center">
          <p className="text-xs text-gray-400">
            SV Büro Koop · Rahmedestraße 65 · 58507 Lüdenscheid · Tel: 02351-123456
          </p>
        </div>
      </footer>
    </div>
  );
}

// ============ DASHBOARD TAB ============
function DashboardTab({ faelle, anfragen, offeneFaelle, inBearbeitung, abgeschlossen, offeneAnfragen, ungeleseneNachrichten, benachrichtigungen, onSelectFall, onNewAnfrage }: {
  faelle: WerkstattFall[]; anfragen: WerkstattAnfrage[];
  offeneFaelle: number; inBearbeitung: number; abgeschlossen: number;
  offeneAnfragen: number; ungeleseneNachrichten: number;
  benachrichtigungen: WerkstattBenachrichtigung[];
  onSelectFall: (f: WerkstattFall) => void; onNewAnfrage: () => void;
}) {
  const recentFaelle = faelle.slice(0, 5);
  const recentBenachrichtigungen = benachrichtigungen.filter(b => !b.gelesen).slice(0, 5);

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard icon={FolderOpen} label="Offene Fälle" value={offeneFaelle} color="text-blue-700" bg="bg-blue-50" />
        <KPICard icon={Clock} label="In Bearbeitung" value={inBearbeitung} color="text-amber-700" bg="bg-amber-50" />
        <KPICard icon={CheckCircle2} label="Abgeschlossen" value={abgeschlossen} color="text-green-700" bg="bg-green-50" />
        <KPICard icon={MessageSquare} label="Neue Nachrichten" value={ungeleseneNachrichten} color="text-purple-700" bg="bg-purple-50" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Cases */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Aktuelle Fälle</h3>
            <span className="text-xs text-gray-400">{faelle.length} gesamt</span>
          </div>
          {recentFaelle.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <FolderOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-400">Noch keine Fälle zugeordnet</p>
              <button onClick={onNewAnfrage} className="mt-3 text-sm text-[#B91C1C] hover:underline flex items-center gap-1 mx-auto">
                <Plus className="w-3.5 h-3.5" /> Neue Anfrage stellen
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {recentFaelle.map(f => (
                <button key={f.id} onClick={() => onSelectFall(f)}
                  className="w-full text-left px-5 py-3 hover:bg-gray-50 transition-colors flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{f.akten_nummer || f.akte_id}</span>
                      <StatusBadge status={f.status || 'unbekannt'} />
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{f.kennzeichen} · {f.fahrzeug}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar: Recent Notifications + Quick Actions */}
        <div className="space-y-4">
          {/* Unread Notifications */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Bell className="w-4 h-4 text-gray-400" /> Neuigkeiten
              </h3>
            </div>
            {recentBenachrichtigungen.length === 0 ? (
              <div className="px-4 py-6 text-center">
                <p className="text-xs text-gray-400">Keine neuen Benachrichtigungen</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {recentBenachrichtigungen.map(b => (
                  <div key={b.id} className="px-4 py-2.5">
                    <p className="text-xs font-medium text-gray-800 truncate">{b.titel}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{formatRelativeTime(b.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Schnellzugriff</h3>
            <button onClick={onNewAnfrage}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[#B91C1C] text-white text-sm font-medium hover:bg-[#991B1B] transition-colors">
              <Plus className="w-4 h-4" /> Neue Anfrage stellen
            </button>
            <button onClick={() => window.open('tel:023511234567')}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
              <Phone className="w-4 h-4 text-gray-400" /> SV Büro anrufen
            </button>
            <button onClick={() => window.open('mailto:info@sv-buero-koop.de')}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
              <Mail className="w-4 h-4 text-gray-400" /> E-Mail schreiben
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ FÄLLE TAB ============
function FaelleTab({ faelle, onSelectFall }: { faelle: WerkstattFall[]; onSelectFall: (f: WerkstattFall) => void }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('alle');

  const filtered = useMemo(() => {
    let result = faelle;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(f =>
        (f.akten_nummer || '').toLowerCase().includes(q) ||
        (f.kennzeichen || '').toLowerCase().includes(q) ||
        (f.fahrzeug || '').toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'alle') {
      result = result.filter(f => f.status === statusFilter);
    }
    return result;
  }, [faelle, search, statusFilter]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Aktenzeichen, Kennzeichen, Fahrzeug suchen..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="w-full sm:w-auto px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none">
          <option value="alle">Alle Status</option>
          <option value="neu">Neu</option>
          <option value="in_bearbeitung">In Bearbeitung</option>
          <option value="gutachten_erstellt">Gutachten erstellt</option>
          <option value="abgeschlossen">Abgeschlossen</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 px-6 py-12 text-center">
          <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">{search ? 'Keine Fälle gefunden' : 'Noch keine Fälle zugeordnet'}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Aktenzeichen</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3 hidden sm:table-cell">Kennzeichen</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3 hidden md:table-cell">Fahrzeug</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Status</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3 hidden lg:table-cell">Datum</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(f => (
                <tr key={f.id} onClick={() => onSelectFall(f)} className="hover:bg-gray-50 cursor-pointer transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{f.akten_nummer || f.akte_id}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">{f.kennzeichen || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">{f.fahrzeug || '—'}</td>
                  <td className="px-4 py-3"><StatusBadge status={f.status || 'unbekannt'} /></td>
                  <td className="px-4 py-3 text-sm text-gray-500 hidden lg:table-cell">{f.datum ? new Date(f.datum).toLocaleDateString('de-DE') : '—'}</td>
                  <td className="px-4 py-3"><ChevronRight className="w-4 h-4 text-gray-300" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============ WISSEN & NEWS TAB ============
function WissenTab() {
  const { artikel, loading } = useWerkstattWissen();
  const [selectedArtikel, setSelectedArtikel] = useState<WerkstattWissen | null>(null);
  const [filterKategorie, setFilterKategorie] = useState<string>('alle');

  // Mobile: Hardware-Back schließt Artikel-Detail statt Seite zu verlassen
  useMobileDetailBackHandler(!!selectedArtikel, () => setSelectedArtikel(null));

  const KATEGORIEN: Record<string, { label: string; color: string; bg: string }> = {
    versicherungsrecht: { label: 'Versicherungsrecht', color: 'text-blue-700', bg: 'bg-blue-50' },
    schadenabwicklung: { label: 'Schadenabwicklung', color: 'text-amber-700', bg: 'bg-amber-50' },
    tipps: { label: 'Tipps', color: 'text-green-700', bg: 'bg-green-50' },
    neuigkeiten: { label: 'Neuigkeiten', color: 'text-purple-700', bg: 'bg-purple-50' },
    technik: { label: 'Technik', color: 'text-cyan-700', bg: 'bg-cyan-50' },
    recht: { label: 'Recht', color: 'text-red-700', bg: 'bg-red-50' },
  };

  const filtered = filterKategorie === 'alle' ? artikel : artikel.filter(a => a.kategorie === filterKategorie);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  // Artikel-Detail-Ansicht
  if (selectedArtikel) {
    const kat = KATEGORIEN[selectedArtikel.kategorie] || { label: selectedArtikel.kategorie, color: 'text-gray-700', bg: 'bg-gray-50' };
    return (
      <div className="max-w-3xl mx-auto">
        <button onClick={() => setSelectedArtikel(null)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft className="w-4 h-4" /> Zurück zur Übersicht
        </button>
        <article className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {selectedArtikel.bild_url && (
            <img src={selectedArtikel.bild_url} alt="" className="w-full h-48 object-cover" />
          )}
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-3">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${kat.bg} ${kat.color}`}>{kat.label}</span>
              <span className="text-xs text-gray-400">{new Date(selectedArtikel.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{selectedArtikel.titel}</h1>
            <p className="text-sm text-gray-500 mb-6">Von {selectedArtikel.autor}</p>
            <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap">
              {selectedArtikel.inhalt.split('\n').map((line, i) => {
                if (line.startsWith('## ')) return <h2 key={i} className="text-lg font-semibold text-gray-900 mt-6 mb-2">{line.replace('## ', '')}</h2>;
                if (line.startsWith('- ')) return <li key={i} className="ml-4 text-gray-700">{line.replace('- ', '')}</li>;
                if (line.trim() === '') return <br key={i} />;
                return <p key={i} className="mb-2">{line}</p>;
              })}
            </div>
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Wissen & News</h2>
        <select value={filterKategorie} onChange={e => setFilterKategorie(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none">
          <option value="alle">Alle Kategorien</option>
          {Object.entries(KATEGORIEN).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 px-6 py-12 text-center">
          <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Noch keine Artikel veröffentlicht</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map(a => {
            const kat = KATEGORIEN[a.kategorie] || { label: a.kategorie, color: 'text-gray-700', bg: 'bg-gray-50' };
            return (
              <button key={a.id} onClick={() => setSelectedArtikel(a)}
                className="text-left bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-all group">
                {a.bild_url && (
                  <img src={a.bild_url} alt="" className="w-full h-36 object-cover group-hover:scale-105 transition-transform duration-300" />
                )}
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${kat.bg} ${kat.color}`}>{kat.label}</span>
                    <span className="text-[10px] text-gray-400">{new Date(a.created_at).toLocaleDateString('de-DE')}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 group-hover:text-[#B91C1C] transition-colors line-clamp-2">{a.titel}</h3>
                  {a.teaser && <p className="text-xs text-gray-500 mt-1.5 line-clamp-2">{a.teaser}</p>}
                  <span className="inline-flex items-center gap-1 text-xs text-[#B91C1C] font-medium mt-3">
                    Weiterlesen <ChevronRight className="w-3 h-3" />
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============ FALL DETAIL ============
function FallDetail({ fall, nachrichten, onBack, onSendMessage, werkstattName }: {
  fall: WerkstattFall;
  nachrichten: any[];
  onBack: () => void;
  onSendMessage: (msg: string) => Promise<any>;
  werkstattName: string;
}) {
  const [activeDetailTab, setActiveDetailTab] = useState<'uebersicht' | 'fahrzeug' | 'beteiligte' | 'dokumente' | 'kommunikation'>('uebersicht');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    const result = await onSendMessage(newMessage);
    setSending(false);
    if (!result?.error) {
      setNewMessage('');
      toast.success('Nachricht gesendet');
    }
  };

  const DETAIL_TABS = [
    { id: 'uebersicht', label: 'Übersicht', icon: Eye },
    { id: 'fahrzeug', label: 'Fahrzeug', icon: Car },
    { id: 'beteiligte', label: 'Beteiligte', icon: Users },
    { id: 'dokumente', label: 'Dokumente', icon: FileText },
    { id: 'kommunikation', label: 'Kommunikation', icon: MessageSquare },
  ];

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft className="w-4 h-4" /> Zurück zur Übersicht
      </button>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-bold text-gray-900">{fall.akten_nummer || fall.akte_id}</h2>
          <StatusBadge status={fall.status || 'unbekannt'} />
        </div>
        <p className="text-sm text-gray-500">{fall.kennzeichen} · {fall.fahrzeug}</p>
      </div>

      {/* Detail Tabs */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex border-b border-gray-100 overflow-x-auto pb-2 sm:pb-0">
            {DETAIL_TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveDetailTab(tab.id as any)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeDetailTab === tab.id ? 'border-[#B91C1C] text-[#B91C1C]' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              <tab.icon className="w-3.5 h-3.5" /> {tab.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {activeDetailTab === 'uebersicht' && (
            <div className="space-y-3">
              <InfoRow label="Aktenzeichen" value={fall.akten_nummer} />
              <InfoRow label="Status" value={fall.status} />
              <InfoRow label="Schadendatum" value={fall.schadendatum ? new Date(fall.schadendatum).toLocaleDateString('de-DE') : undefined} />
              <InfoRow label="Erstellt am" value={fall.datum ? new Date(fall.datum).toLocaleDateString('de-DE') : undefined} />
              <InfoRow label="Betrieb" value={fall.betrieb} />
            </div>
          )}

          {activeDetailTab === 'fahrzeug' && (
            <div className="space-y-3">
              <InfoRow label="Kennzeichen" value={fall.kennzeichen} />
              <InfoRow label="Fahrzeug" value={fall.fahrzeug} />
              <InfoRow label="Auftraggeber" value={fall.auftraggeber_name} />
            </div>
          )}

          {activeDetailTab === 'beteiligte' && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2"><Building2 className="w-4 h-4 text-gray-400" /> Sachverständigenbüro</h4>
                <p className="text-sm text-gray-700">SV Büro Koop</p>
                <p className="text-xs text-gray-500">Rahmedestraße 65, 58507 Lüdenscheid</p>
                <p className="text-xs text-gray-500">Tel: 02351-123456</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2"><Wrench className="w-4 h-4 text-gray-400" /> Werkstatt</h4>
                <p className="text-sm text-gray-700">{werkstattName}</p>
              </div>
            </div>
          )}

          {activeDetailTab === 'dokumente' && (
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Dokumente werden nach Fertigstellung hier bereitgestellt.</p>
                <p className="text-xs text-gray-400 mt-1">Gutachten, Kalkulationen und Lichtbilder zum Download.</p>
              </div>
            </div>
          )}

          {activeDetailTab === 'kommunikation' && (
            <div className="space-y-4">
              <div className="max-h-64 overflow-y-auto space-y-2">
                {nachrichten.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">Noch keine Nachrichten</p>
                ) : (
                  nachrichten.map(n => (
                    <div key={n.id} className={`flex ${n.absender === 'werkstatt' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                        n.absender === 'werkstatt' ? 'bg-[#B91C1C] text-white' : 'bg-gray-100 text-gray-800'
                      }`}>
                        <p>{n.nachricht}</p>
                        <p className={`text-[10px] mt-1 ${n.absender === 'werkstatt' ? 'text-red-200' : 'text-gray-400'}`}>
                          {new Date(n.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="flex gap-2">
                <input value={newMessage} onChange={e => setNewMessage(e.target.value)}
                  placeholder="Nachricht schreiben..."
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none" />
                <button onClick={handleSend} disabled={sending || !newMessage.trim()}
                  className="px-4 py-2.5 bg-[#B91C1C] text-white rounded-lg text-sm font-medium hover:bg-[#991B1B] transition-colors disabled:opacity-50 flex items-center gap-1.5">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ ANFRAGE TAB ============
function AnfrageTab({ onSubmit, onSuccess }: {
  onSubmit: (a: Partial<WerkstattAnfrage>) => Promise<any>;
  onSuccess: () => void;
}) {
  const [typ, setTyp] = useState<'kostenvoranschlag' | 'gutachten' | 'sonstiges'>('kostenvoranschlag');
  const [kennzeichen, setKennzeichen] = useState('');
  const [fahrzeug, setFahrzeug] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!kennzeichen || !beschreibung) { toast.error('Bitte Pflichtfelder ausfüllen'); return; }
    setSubmitting(true);
    const result = await onSubmit({ typ, kennzeichen: kennzeichen.toUpperCase(), fahrzeug, schadenbeschreibung: beschreibung, fotos: [] });
    setSubmitting(false);
    if (!result?.error) {
      toast.success('Anfrage erfolgreich gesendet!');
      onSuccess();
    } else {
      toast.error('Fehler beim Senden der Anfrage');
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Neue Anfrage stellen</h2>
        <p className="text-sm text-gray-500 mb-6">Stellen Sie eine Anfrage für einen Kostenvoranschlag oder ein Gutachten.</p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Art der Anfrage *</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                { id: 'kostenvoranschlag', label: 'Kostenvoranschlag', icon: FileCheck },
                { id: 'gutachten', label: 'Gutachten', icon: FileText },
                { id: 'sonstiges', label: 'Sonstiges', icon: MessageSquare },
              ].map(t => (
                <button key={t.id} type="button" onClick={() => setTyp(t.id as any)}
                  className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-lg border text-sm transition-colors ${
                    typ === t.id ? 'border-[#B91C1C] bg-red-50 text-[#B91C1C]' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}>
                  <t.icon className="w-5 h-5" />
                  <span className="text-xs font-medium">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kennzeichen *</label>
              <input value={kennzeichen} onChange={e => setKennzeichen(e.target.value)}
                placeholder="MK-AB 1234" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none uppercase" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fahrzeug</label>
              <input value={fahrzeug} onChange={e => setFahrzeug(e.target.value)}
                placeholder="z.B. BMW 320d" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Schadenbeschreibung *</label>
            <textarea value={beschreibung} onChange={e => setBeschreibung(e.target.value)} rows={4}
              placeholder="Beschreiben Sie den Schaden möglichst genau..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none resize-none" />
          </div>

          <div className="bg-gray-50 rounded-lg p-4 border border-dashed border-gray-300 text-center">
            <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Foto-Upload wird in einer zukünftigen Version verfügbar sein.</p>
            <p className="text-xs text-gray-400 mt-1">Senden Sie Fotos alternativ per E-Mail an info@sv-buero-koop.de</p>
          </div>

          <button type="submit" disabled={submitting}
            className="w-full bg-[#B91C1C] text-white py-3 rounded-lg text-sm font-medium hover:bg-[#991B1B] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Anfrage absenden
          </button>
        </form>
      </div>
    </div>
  );
}

// ============ PROFIL TAB ============
function ProfilTab({ werkstatt, onUpdate }: {
  werkstatt: any;
  onUpdate: (u: any) => Promise<any>;
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    ansprechpartner: werkstatt.ansprechpartner || '',
    telefon: werkstatt.telefon || '',
    email: werkstatt.email || '',
    adresse: werkstatt.adresse || '',
    plz: werkstatt.plz || '',
    ort: werkstatt.ort || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const result = await onUpdate(form);
    setSaving(false);
    if (!result?.error) {
      toast.success('Profil aktualisiert');
      setEditing(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{werkstatt.name}</h2>
            <p className="text-sm text-gray-500">Werkstatt-Profil</p>
          </div>
          {!editing && (
            <button onClick={() => setEditing(true)}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">
              Bearbeiten
            </button>
          )}
        </div>

        {editing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Ansprechpartner</label>
                <input value={form.ansprechpartner} onChange={e => setForm(p => ({ ...p, ansprechpartner: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Telefon</label>
                <input value={form.telefon} onChange={e => setForm(p => ({ ...p, telefon: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">E-Mail</label>
                <input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Adresse</label>
                <input value={form.adresse} onChange={e => setForm(p => ({ ...p, adresse: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">PLZ</label>
                <input value={form.plz} onChange={e => setForm(p => ({ ...p, plz: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Ort</label>
                <input value={form.ort} onChange={e => setForm(p => ({ ...p, ort: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none" />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={handleSave} disabled={saving}
                className="px-4 py-2 bg-[#B91C1C] text-white rounded-lg text-sm font-medium hover:bg-[#991B1B] transition-colors disabled:opacity-50 flex items-center gap-1.5">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Speichern
              </button>
              <button onClick={() => setEditing(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                Abbrechen
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <InfoRow label="Ansprechpartner" value={werkstatt.ansprechpartner} />
            <InfoRow label="Telefon" value={werkstatt.telefon} />
            <InfoRow label="E-Mail" value={werkstatt.email} />
            <InfoRow label="Adresse" value={werkstatt.adresse} />
            <InfoRow label="PLZ / Ort" value={`${werkstatt.plz || ''} ${werkstatt.ort || ''}`.trim() || undefined} />
            <InfoRow label="Mitglied seit" value={new Date(werkstatt.created_at).toLocaleDateString('de-DE')} />
          </div>
        )}
      </div>
    </div>
  );
}

// ============ HELPER COMPONENTS ============
function KPICard({ icon: Icon, label, value, color, bg }: { icon: any; label: string; value: number; color: string; bg: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 ${bg} rounded-lg flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; bg: string; text: string }> = {
    neu: { label: 'Neu', bg: 'bg-blue-50', text: 'text-blue-700' },
    in_bearbeitung: { label: 'In Bearbeitung', bg: 'bg-amber-50', text: 'text-amber-700' },
    gutachten_erstellt: { label: 'Gutachten erstellt', bg: 'bg-purple-50', text: 'text-purple-700' },
    abgeschlossen: { label: 'Abgeschlossen', bg: 'bg-green-50', text: 'text-green-700' },
    archiviert: { label: 'Archiviert', bg: 'bg-gray-100', text: 'text-gray-600' },
  };
  const c = config[status] || { label: status, bg: 'bg-gray-100', text: 'text-gray-600' };
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${c.bg} ${c.text}`}>{c.label}</span>;
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value || '—'}</span>
    </div>
  );
}

function getNotifIcon(typ: string) {
  switch (typ) {
    case 'gutachten_fertig': return <FileCheck className="w-4 h-4 text-green-600" />;
    case 'status_aenderung': return <Clock className="w-4 h-4 text-amber-600" />;
    case 'neue_nachricht': return <MessageSquare className="w-4 h-4 text-blue-600" />;
    case 'anfrage_beantwortet': return <CheckCircle2 className="w-4 h-4 text-purple-600" />;
    case 'dokument_hochgeladen': return <Download className="w-4 h-4 text-cyan-600" />;
    case 'newsletter': return <BookOpen className="w-4 h-4 text-red-600" />;
    default: return <Bell className="w-4 h-4 text-gray-500" />;
  }
}

function getNotifIconBg(typ: string) {
  switch (typ) {
    case 'gutachten_fertig': return 'bg-green-50';
    case 'status_aenderung': return 'bg-amber-50';
    case 'neue_nachricht': return 'bg-blue-50';
    case 'anfrage_beantwortet': return 'bg-purple-50';
    case 'dokument_hochgeladen': return 'bg-cyan-50';
    case 'newsletter': return 'bg-red-50';
    default: return 'bg-gray-50';
  }
}

function formatRelativeTime(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Gerade eben';
  if (diffMin < 60) return `Vor ${diffMin} Min.`;
  if (diffHrs < 24) return `Vor ${diffHrs} Std.`;
  if (diffDays < 7) return `Vor ${diffDays} Tagen`;
  return date.toLocaleDateString('de-DE');
}
